import type { ImpressionViewAncillariesItem } from '@/scopes/checkout/domain/analytics/types'
import * as React from 'react'
import { useCheckoutSid } from '@/scopes/checkout/domain/hooks'

const STORAGE_KEY_SUFFIX = 'eec-submit-selected-services'
const HAS_SUBMITTED_STORAGE_KEY_SUFFIX = 'eec-submit-has-submitted'
const REMOVED_ITEMS_STORAGE_KEY_SUFFIX = 'eec-remove-from-cart-removed-items'

function safeParseItems(value: string | null): ImpressionViewAncillariesItem[] {
  if (!value)
    return []

  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  }
  catch {
    return []
  }
}

function upsertItems(items: ImpressionViewAncillariesItem[], item: ImpressionViewAncillariesItem) {
  const index = items.findIndex(currentItem => currentItem.item_id === item.item_id)

  if (index === -1)
    return [...items, item]

  const nextItems = [...items]
  nextItems[index] = item
  return nextItems
}

export function useSelectedServicesStorage() {
  const sid = useCheckoutSid()

  const storageKey = React.useMemo(
    () => (sid ? `${sid}-${STORAGE_KEY_SUFFIX}` : ''),
    [sid],
  )
  const hasSubmittedStorageKey = React.useMemo(
    () => (sid ? `${sid}-${HAS_SUBMITTED_STORAGE_KEY_SUFFIX}` : ''),
    [sid],
  )
  const removedItemsStorageKey = React.useMemo(
    () => (sid ? `${sid}-${REMOVED_ITEMS_STORAGE_KEY_SUFFIX}` : ''),
    [sid],
  )

  const getItems = React.useCallback(() => {
    if (!storageKey || typeof window === 'undefined')
      return []

    return safeParseItems(window.sessionStorage.getItem(storageKey))
  }, [storageKey])

  const addItem = React.useCallback((item: ImpressionViewAncillariesItem) => {
    if (!storageKey || typeof window === 'undefined')
      return

    const currentItems = safeParseItems(window.sessionStorage.getItem(storageKey))
    window.sessionStorage.setItem(storageKey, JSON.stringify(upsertItems(currentItems, item)))
  }, [storageKey])

  const removeItem = React.useCallback((itemId: string) => {
    if (!storageKey || typeof window === 'undefined')
      return

    const currentItems = safeParseItems(window.sessionStorage.getItem(storageKey))
    const nextItems = currentItems.filter(item => item.item_id !== itemId)
    window.sessionStorage.setItem(storageKey, JSON.stringify(nextItems))
  }, [storageKey])

  const clearItems = React.useCallback(() => {
    if (!storageKey || typeof window === 'undefined')
      return

    window.sessionStorage.removeItem(storageKey)
  }, [storageKey])

  const hasSubmittedToStep2 = React.useCallback(() => {
    if (!hasSubmittedStorageKey || typeof window === 'undefined')
      return false

    return window.sessionStorage.getItem(hasSubmittedStorageKey) === 'true'
  }, [hasSubmittedStorageKey])

  const markSubmittedToStep2 = React.useCallback(() => {
    if (!hasSubmittedStorageKey || typeof window === 'undefined')
      return

    window.sessionStorage.setItem(hasSubmittedStorageKey, 'true')
  }, [hasSubmittedStorageKey])

  const getRemovedItems = React.useCallback(() => {
    if (!removedItemsStorageKey || typeof window === 'undefined')
      return []

    return safeParseItems(window.sessionStorage.getItem(removedItemsStorageKey))
  }, [removedItemsStorageKey])

  const trackRemovedItem = React.useCallback((item: ImpressionViewAncillariesItem) => {
    if (!removedItemsStorageKey || typeof window === 'undefined')
      return

    const currentItems = safeParseItems(window.sessionStorage.getItem(removedItemsStorageKey))
    window.sessionStorage.setItem(removedItemsStorageKey, JSON.stringify(upsertItems(currentItems, item)))
  }, [removedItemsStorageKey])

  const clearRemovedItems = React.useCallback(() => {
    if (!removedItemsStorageKey || typeof window === 'undefined')
      return

    window.sessionStorage.removeItem(removedItemsStorageKey)
  }, [removedItemsStorageKey])

  return {
    addItem,
    removeItem,
    getItems,
    clearItems,
    hasSubmittedToStep2,
    markSubmittedToStep2,
    getRemovedItems,
    trackRemovedItem,
    clearRemovedItems,
  }
}
